import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Card,
  Empty,
  PageHeader,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
  Banner,
} from '@/components/ui';
import { useAuth } from '@/store/auth';

interface ResourceRow {
  id?: string;
  staffId?: string;
  name: string;
  status?: string;
  bookedMinutes: number;
  availableMinutes?: number;
  capacityMinutes?: number;
  appointments?: number;
  utilizationPct: number | null;
}
interface Util {
  range: { from: string; to: string; days: number };
  operatingHoursPerDay: number;
  doctors: ResourceRow[];
  rooms: ResourceRow[];
  devices: ResourceRow[];
}

const hrs = (m: number) => (m / 60).toFixed(1);
// 利用率越低 = 閒置浪費(營運長要看的),用紅;高 = 充分利用,用綠
const tone = (p: number | null): Parameters<typeof Tag>[0]['tone'] =>
  p == null ? 'default' : p >= 65 ? 'success' : p >= 35 ? 'warn' : 'danger';

function UtilBar({ pct }: { pct: number | null }) {
  if (pct == null) return <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>無排班</span>;
  const color = pct >= 65 ? 'var(--success)' : pct >= 35 ? 'var(--warn)' : 'var(--danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, maxWidth: 120, height: 8, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <Tag tone={tone(pct)}>{pct}%</Tag>
    </div>
  );
}

const fromDefault = () => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
const toDefault = () => new Date().toISOString().slice(0, 10);

export function UtilizationPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const [from, setFrom] = useState(fromDefault());
  const [to, setTo] = useState(toDefault());

  const q = useQuery({
    queryKey: ['utilization', from, to],
    queryFn: () => api.get<Util>(`/scheduling/utilization?from=${from}&to=${to}`),
    enabled: isAdmin && !!from && !!to,
  });

  const toggleMaint = useMutation({
    mutationFn: (v: { kind: 'rooms' | 'devices'; id: string; status: 'ok' | 'maint' }) =>
      api.patch(`/catalog/${v.kind}/${v.id}/status`, { status: v.status }),
    onSuccess: (_d, v) => {
      ok(v.status === 'maint' ? '已設為維修中(該資源停止排程)' : '已恢復正常');
      void qc.invalidateQueries({ queryKey: ['utilization'] });
    },
    onError: fail,
  });
  const MaintCell = ({ kind, id, status }: { kind: 'rooms' | 'devices'; id: string; status?: string }) => {
    const maint = status === 'maint';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {maint ? <Tag tone="warn">維修中</Tag> : <Tag tone="success">正常</Tag>}
        <button
          className="btn btn-ghost"
          style={{ fontSize: 11, padding: '3px 8px' }}
          disabled={toggleMaint.isPending}
          onClick={() => toggleMaint.mutate({ kind, id, status: maint ? 'ok' : 'maint' })}
        >
          {maint ? '恢復正常' : '設為維修'}
        </button>
      </div>
    );
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="產能利用率" subtitle="醫師工時 · 診間 · 儀器佔用率" />
        <Card><Empty>🔒 產能屬管理資料,僅租戶管理者可檢視。</Empty></Card>
      </div>
    );
  }

  const d = q.data;

  return (
    <div>
      <PageHeader
        title="產能利用率"
        subtitle="閒置 = 浪費的成本。看醫師工時、診間、儀器有沒有被充分利用,決定排班與擴點"
        right={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: 150 }} />
            <span style={{ color: 'var(--ink-3)' }}>~</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: 150 }} />
          </div>
        }
      />

      <Banner msg={msg} />

      <Card title="醫師工時利用率" icon="🩺">
        {!d?.doctors.length ? (
          <Empty>此期間無醫師資料。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>醫師</th>
                <th style={thStyle}>可用工時</th>
                <th style={thStyle}>已排工時</th>
                <th style={thStyle}>預約數</th>
                <th style={thStyle}>利用率</th>
              </tr>
            </thead>
            <tbody>
              {d.doctors.map((r) => (
                <tr key={r.staffId}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{r.name}</td>
                  <td style={cellStyle} className="num">{hrs(r.availableMinutes ?? 0)} 時</td>
                  <td style={cellStyle} className="num">{hrs(r.bookedMinutes)} 時</td>
                  <td style={cellStyle} className="num">{r.appointments}</td>
                  <td style={cellStyle}><UtilBar pct={r.utilizationPct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="診間佔用率" icon="🚪">
          {!d?.rooms.length ? <Empty>無診間。</Empty> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>診間</th><th style={thStyle}>已用</th><th style={thStyle}>佔用率</th><th style={thStyle}>維修</th></tr></thead>
              <tbody>
                {d.rooms.map((r) => (
                  <tr key={r.id} style={{ opacity: r.status === 'maint' ? 0.6 : 1 }}>
                    <td style={{ ...cellStyle, fontWeight: 600 }}>{r.name}</td>
                    <td style={cellStyle} className="num">{hrs(r.bookedMinutes)} 時</td>
                    <td style={cellStyle}><UtilBar pct={r.utilizationPct} /></td>
                    <td style={cellStyle}><MaintCell kind="rooms" id={r.id!} status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="儀器佔用率" icon="🔬">
          {!d?.devices.length ? <Empty>無儀器。</Empty> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>儀器</th><th style={thStyle}>已用</th><th style={thStyle}>佔用率</th><th style={thStyle}>維修</th></tr></thead>
              <tbody>
                {d.devices.map((r) => (
                  <tr key={r.id} style={{ opacity: r.status === 'maint' ? 0.6 : 1 }}>
                    <td style={{ ...cellStyle, fontWeight: 600 }}>{r.name}</td>
                    <td style={cellStyle} className="num">{hrs(r.bookedMinutes)} 時</td>
                    <td style={cellStyle}><UtilBar pct={r.utilizationPct} /></td>
                    <td style={cellStyle}><MaintCell kind="devices" id={r.id!} status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8 }}>
        ※ 醫師可用工時取自排班(全班 8 時 / 早晚班 4 時);診間 / 儀器分母為營業時段{' '}
        {d?.operatingHoursPerDay ?? 11} 時 × {d?.range.days ?? 0} 天。<b style={{ color: 'var(--danger)' }}>紅色 = 閒置偏高</b>,代表產能被浪費。
      </div>
    </div>
  );
}
